import { forwardRef, useState, useEffect, useCallback, useImperativeHandle, useRef } from 'react';
import type { Ref, PropsWithoutRef, ReactNode, ChangeEvent, RefObject, KeyboardEvent } from 'react';
import styled, { css } from 'styled-components';

import {
  Flex,
  TextArea,
  Button,
  Icon,
  registerIcon,
  Popover,
  EmojiPicker,
  hasProp,
  useI18n,
  useElement,
  defaultThemeProp,
  useOuterEvent,
  VisuallyHiddenText,
  AIButton,
  FileList,
  useAfterInitialEffect
} from '@pega/cosmos-react-core';
import type {
  EmojiPickerProps,
  PropsWithDefaults,
  ForwardRefForwardPropsComponent,
  FileItemProps
} from '@pega/cosmos-react-core';
import BareButton from '@pega/cosmos-react-core/lib/components/Button/BareButton';
import * as timesIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/times.icon';
import * as faceHappyIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/face-happy.icon';
import * as paperClipIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/paper-clip.icon';
import { StyledFileList } from '@pega/cosmos-react-core/lib/components/File/FileList';

registerIcon(timesIcon, faceHappyIcon, paperClipIcon);

export type SpecialKeysConfig = {
  /** Collection of specialKeys */
  keys: string[];
  /** onSpecialKey is fired when a special key is typed in Textarea */
  onSpecialKey: (e: KeyboardEvent<HTMLTextAreaElement>, key: string) => void;
};

export interface ChatComposerProps {
  /** Place holder */
  placeholder?: string;
  /** Default message to populate on load */
  defaultMessage?: string;
  /** Max character limit */
  maxLength?: number;
  /** Callback when send button is clicked */
  onSend: (message: string) => void;
  /** Enter / Return key sends message */
  enterKeySendsMessage?: boolean;
  /** Max number of attachments to be allowed in chat composer.
   * If zero is passed, add attachments icon will be hidden. same rule applies if prop is not passed.
   * If attachments added are >= maxAttachments, add attachments icon will be disabled.
   */
  maxAttachments?: number;
  /** show/hide emoji */
  showEmoji?: boolean;
  /** Attachments list */
  attachments?: FileItemProps[];
  /** Callback when attachment icon is clicked */
  onAddAttachment?: () => void;
  /** Special Keys configuration */
  specialKeysConfig?: SpecialKeysConfig;
  /** Disables the composer area */
  disabled?: boolean;
  /** Children */
  children?: ReactNode;
  /** On suggest reply click */
  onSuggestReplyClick?: () => void;
  /**
   * Disable suggest reply button
   * @default false
   */
  disableSuggestReply?: boolean;
  /** On join coversation click */
  joinConversation?: {
    onClick: () => void;
    disabled?: boolean;
  };
  /** Ref to the Imperative handle */
  ref?: Ref<ChatComposerImperativeHandleProps>;
}

const StyledAttachmentButton = styled(Button)<{ maxAttachmentsReached: boolean }>(
  ({ theme, maxAttachmentsReached }) =>
    maxAttachmentsReached
      ? css`
          opacity: ${theme.base['disabled-opacity']};
          cursor: not-allowed;
        `
      : null
);

StyledAttachmentButton.defaultProps = defaultThemeProp;

const StyledPreviewButton = styled(BareButton)``;
const StyledDeleteButton = styled(BareButton)``;

const StyledChatComposer = styled.footer<PropsWithDefaults<ChatComposerProps, 'disabled'>>(
  ({
    theme,
    disabled,
    theme: {
      base: { 'disabled-opacity': transparentBg, spacing }
    }
  }) => {
    return css`
      padding: calc(2 * ${spacing});
      background-color: ${theme.base.palette['secondary-background']};
      border-end-start-radius: inherit;
      border-end-end-radius: inherit;
      textarea {
        border-radius: ${theme.base['border-radius']};
        height: 5rem;
        border: 0.0625rem solid ${theme.base.colors.gray['extra-light']};
        max-width: 100%;
      }
      ${StyledFileList} {
        ${disabled &&
        css`
          pointer-events: none;
          opacity: ${transparentBg};
        `}
      }

      ${StyledPreviewButton} {
        color: ${theme.base.palette.interactive};
        font-weight: bold;
        &:hover {
          text-decoration: underline;
        }
      }

      ${StyledDeleteButton} {
        color: ${theme.base.palette.interactive};
      }
    `;
  }
);

StyledChatComposer.defaultProps = defaultThemeProp;

export const StyledAttachmentsListContainer = styled(FileList)(({ theme }) => {
  return css`
    max-height: 5rem;
    overflow-y: auto;

    &:focus-within {
      box-shadow: ${theme.base.shadow['focus-group']};
      border-radius: calc(0.5 * ${theme.base['border-radius']});
    }
  `;
});

StyledAttachmentsListContainer.defaultProps = defaultThemeProp;

export const StyledJoinConversation = styled(Button)`
  margin: auto;
`;

export interface ChatComposerImperativeHandleProps {
  /** Function to append a message */
  appendToMessage: (
    appendMessage: string,
    position?: 'cursor' | 'start' | 'end' | 'specialKey'
  ) => void;
  /** Actual ref to Chat composer component */
  chatComposerRef: RefObject<HTMLDivElement | undefined>;
  textAreaRef: RefObject<HTMLTextAreaElement | undefined>;
}

const ChatComposer: ForwardRefForwardPropsComponent<ChatComposerProps> = forwardRef(
  function ChatComposer(
    {
      children,
      attachments = [],
      maxAttachments = 0,
      onAddAttachment,
      maxLength,
      placeholder: defaultPlaceHolder,
      defaultMessage = '',
      onSend,
      specialKeysConfig,
      disabled = false,
      enterKeySendsMessage = true,
      showEmoji,
      onSuggestReplyClick,
      disableSuggestReply = false,
      joinConversation,
      ...restProps
    }: PropsWithoutRef<ChatComposerProps>,
    ref: ChatComposerProps['ref']
  ) {
    const [message, setMessage] = useState(defaultMessage);
    useEffect(() => {
      setMessage(defaultMessage);
    }, [defaultMessage]);

    const t = useI18n();

    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [emojiBtnEl, setEmojiBtnEl] = useElement();
    const [announcement, setAnnouncement] = useState('');
    const lastEnteredSpecialKey = useRef<string | null>(null);
    const maxAttachmentsReached = attachments.length >= maxAttachments;

    const appendToMessage = useCallback(
      (
        appendMessage: string,
        position?: 'cursor' | 'start' | 'end' | 'specialKey',
        selectText: boolean = true
      ) => {
        let selectionStart;
        let selectionEnd;
        if (textAreaRef.current) {
          if (position === 'start') {
            textAreaRef.current.value = appendMessage + textAreaRef.current.value;
            selectionStart = 0;
            selectionEnd = appendMessage.length;
          } else if (position === 'end') {
            const initialEnd = textAreaRef.current.value.length;
            textAreaRef.current.value += appendMessage;
            selectionStart = initialEnd;
            selectionEnd = initialEnd + appendMessage.length;
          } else if (position === 'specialKey' && lastEnteredSpecialKey.current) {
            const replaceChars = lastEnteredSpecialKey.current.length;
            const currentSelectionStart = textAreaRef.current.selectionStart;
            textAreaRef.current.value =
              textAreaRef.current.value.substring(
                0,
                textAreaRef.current.selectionStart - replaceChars
              ) +
              appendMessage +
              textAreaRef.current.value.substring(
                textAreaRef.current.selectionStart,
                textAreaRef.current.value.length
              );
            selectionStart = currentSelectionStart - replaceChars;
            selectionEnd = currentSelectionStart + appendMessage.length - replaceChars;
            lastEnteredSpecialKey.current = null;
          } else {
            const currentSelectionStart = textAreaRef.current.selectionStart;
            textAreaRef.current.value =
              textAreaRef.current.value.substring(0, textAreaRef.current.selectionStart) +
              appendMessage +
              textAreaRef.current.value.substring(
                textAreaRef.current.selectionStart,
                textAreaRef.current.value.length
              );
            selectionStart = currentSelectionStart;
            selectionEnd = currentSelectionStart + appendMessage.length;
          }
          textAreaRef.current.selectionStart = selectText ? selectionStart : selectionEnd;
          textAreaRef.current.selectionEnd = selectionEnd;
          textAreaRef.current.focus();
          setMessage(textAreaRef.current.value);
        }
      },
      [setMessage, textAreaRef.current]
    );

    const chatComposerRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({ appendToMessage, chatComposerRef, textAreaRef }), [
      appendToMessage,
      chatComposerRef.current,
      textAreaRef.current
    ]);

    const sendMessage = useCallback(
      (newMessage: string) => {
        if (onSend) {
          onSend(newMessage);
          setMessage('');
        }
      },
      [onSend]
    );

    const handleEnterKeyDown = useCallback(
      (e: KeyboardEvent) => {
        if (enterKeySendsMessage && e.key === 'Enter' && !e.shiftKey) {
          if (!maxLength || message.length <= maxLength) {
            sendMessage(message);
            setAnnouncement(t('message_sent'));
          }
          e.preventDefault();
          e.stopPropagation();
        }
      },
      [onSend, message, maxLength]
    );

    const handleTextAreaChange = useCallback(
      (e: ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(e.currentTarget.value);
        setAnnouncement('');
      },
      [setMessage]
    );

    const handleSpecialKey = useCallback(
      (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (!specialKeysConfig) return;
        const { keys, onSpecialKey } = specialKeysConfig;
        if (specialKeysConfig && keys.length > 0) {
          const composerValue = e.currentTarget.value.substring(0, e.currentTarget.selectionStart);
          const specialKey = keys.find(key => composerValue.endsWith(key));
          if (specialKey) {
            lastEnteredSpecialKey.current = specialKey;
            onSpecialKey(e, specialKey);
          }
        }
      },
      [specialKeysConfig]
    );

    const toggleEmoji = () => {
      setShowEmojiPicker(!showEmojiPicker);
    };
    const onEmojiSelect: EmojiPickerProps['onSelect'] = emojiElem => {
      // Check that it is not a custom emoji
      if (hasProp(emojiElem, 'native')) {
        appendToMessage(emojiElem.native, 'cursor', false);
      }
      setShowEmojiPicker(false);
    };

    const EmojiPickerRef = useRef<HTMLDivElement>(null);

    useOuterEvent('mousedown', [emojiBtnEl, EmojiPickerRef], () => {
      setShowEmojiPicker(false);
    });

    useAfterInitialEffect(() => {
      textAreaRef.current?.focus();
    }, [!!joinConversation]);

    return (
      <>
        <Flex
          as={StyledChatComposer}
          disabled={disabled}
          container={{
            direction: 'column',
            gap: 2
          }}
          item={{
            shrink: 0
          }}
          {...restProps}
          ref={chatComposerRef}
        >
          {joinConversation ? (
            <StyledJoinConversation
              variant='primary'
              onClick={() => {
                joinConversation.onClick();
              }}
              disabled={joinConversation.disabled}
            >
              {t('join_conversation')}
            </StyledJoinConversation>
          ) : (
            <>
              <Flex
                container={{
                  direction: 'column',
                  gap: 0.5
                }}
              >
                <StyledAttachmentsListContainer
                  type='item'
                  role='group'
                  aria-label={`${t('attachments')}`}
                  items={
                    disabled
                      ? attachments.map(item => {
                          return {
                            ...item,
                            onDelete: undefined,
                            onDownload: undefined,
                            onPreview: undefined,
                            onEdit: undefined,
                            onCancel: undefined
                          };
                        })
                      : attachments
                  }
                />
                <TextArea
                  ref={textAreaRef}
                  maxLength={maxLength}
                  displayCharCount={maxLength !== undefined}
                  hardStop={false}
                  placeholder={defaultPlaceHolder}
                  value={message}
                  disabled={disabled}
                  onKeyUp={handleSpecialKey}
                  onKeyDown={handleEnterKeyDown}
                  onChange={handleTextAreaChange}
                  onFocus={handleTextAreaChange}
                  aria-label={t('enter_message')}
                  autoResize={false}
                />
              </Flex>
              <Flex container={{ gap: 0.5 }}>
                {showEmoji && (
                  <Button
                    ref={setEmojiBtnEl}
                    onClick={toggleEmoji}
                    variant='simple'
                    disabled={disabled}
                    icon
                    aria-label={t('add_emoji')}
                  >
                    <Icon name='face-happy' />
                  </Button>
                )}
                {maxAttachments > 0 && (
                  <div>
                    <StyledAttachmentButton
                      onClick={maxAttachmentsReached ? undefined : onAddAttachment}
                      variant='simple'
                      maxAttachmentsReached={maxAttachmentsReached}
                      disabled={disabled}
                      icon
                      label={
                        maxAttachmentsReached
                          ? t('email_disable_attachment', [maxAttachments])
                          : t('email_add_attachment')
                      }
                      aria-label={t(
                        maxAttachmentsReached
                          ? 'email_disable_attachment'
                          : 'email_add_attachment_count',
                        [maxAttachments]
                      )}
                    >
                      <Icon name='paper-clip' />
                    </StyledAttachmentButton>
                  </div>
                )}
                <Flex item={{ grow: 1 }} container={{ gap: 1 }}>
                  {children}
                  {onSuggestReplyClick && (
                    <AIButton
                      label={t('suggest_reply')}
                      disabled={disabled || disableSuggestReply}
                      onClick={onSuggestReplyClick}
                    />
                  )}
                </Flex>

                <Button
                  variant='primary'
                  disabled={disabled || (!!maxLength && message.length > maxLength)}
                  onClick={() => {
                    sendMessage(message);
                    setAnnouncement(t('message_sent'));
                  }}
                >
                  {t('send')}
                </Button>
                {announcement && (
                  <VisuallyHiddenText aria-live='assertive'>{announcement}</VisuallyHiddenText>
                )}
              </Flex>
            </>
          )}
        </Flex>
        <Popover
          show={showEmojiPicker}
          target={emojiBtnEl}
          placement='top-start'
          modifiers={[
            {
              name: 'flip',
              enabled: true
            }
          ]}
        >
          <EmojiPicker ref={EmojiPickerRef} onSelect={onEmojiSelect} />
        </Popover>
      </>
    );
  }
);

export default ChatComposer;
